"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { toast, Slide } from "react-toastify";
import { useAuth } from "@/context/AuthProvider";
import { isDealerB2BEligible } from "@/app/helpers/dealerAccess";
import { getDealerAuthHeaders } from "@/app/helpers/getDealerAuthHeaders";
import { getInvoiceContactIdForFrontend } from "@/app/helpers/devInvoiceContactOverride";
import { useFetchWonVehicles } from "@/app/helpers/useFetchWonVehicles";
import useBackendUrl from "@/hooks/useBackendUrl";
import counterOfferIcon from "@/assets/svgs/gebote.svg";
import infoIcon from "@/assets/svgs/radioIcon.svg";
import closeIcon from "@/assets/svgs/close.svg";
import { consumeSalesEvents, isSalesActivityResponse } from "./salesActivityState";
import "@/components/ToastNotification/Toast.css";

const EMPTY = {
  tasks: [],
  loading: false,
  refreshing: false,
  error: "",
  retry: () => {},
  overviewWonVehicles: [],
  overdueInvoices: [],
  overviewTaskCount: 0,
  hasOverviewTask: false,
  overviewTasksLoading: false,
};
const ActivityContext = createContext(EMPTY);

const toDate = (value) => {
  if (!value && value !== 0) return null;
  if (typeof value === "number") return new Date(value * 1000);
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

export const getOverviewInvoiceDueDate = (invoice) => {
  const explicitDueDate = toDate(invoice?.dueDate);
  if (explicitDueDate) return explicitDueDate;
  const invoiceDate = toDate(invoice?.invoiceDate);
  if (!invoiceDate) return null;
  return new Date(invoiceDate.getTime() + 7 * 24 * 60 * 60 * 1000);
};

const isOverviewInvoiceOverdue = (invoice) => {
  const status = Number.parseInt(invoice?.status, 10);
  if (status === 1000 || status === 1024) return false;
  const dueDate = getOverviewInvoiceDueDate(invoice);
  if (!dueDate) return false;
  const now = new Date();
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999,
  );
  return dueDate.getTime() <= endOfToday.getTime();
};

function CloseButton({ closeToast }) {
  return (
    <button
      type="button"
      aria-label="Schließen"
      className="tasks-toast__close"
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        closeToast();
      }}
    >
      <Image src={closeIcon} alt="" width={16} height={16} />
    </button>
  );
}

function showEvent(event, toastId) {
  const isCounterOffer = event.type === "counter-offer";
  const content = isCounterOffer ? (
    <Link
      href={event.href}
      className="tasks-toast__eventLink"
      aria-label={`${event.title}: ${event.body} Jetzt ansehen`}
      onClick={() => toast.dismiss(toastId)}
    >
      <span className="tasks-toast__content tasks-toast__content--salesEvent">
        <Image
          src={counterOfferIcon}
          alt=""
          width={28}
          height={28}
          className="tasks-toast__eventIcon"
        />
        <span className="tasks-toast__eventCopy">
          <strong className="tasks-toast__eventTitle">{event.title}</strong>
          <span className="tasks-toast__eventBody">{event.body}</span>
          <span className="tasks-toast__eventAction">Jetzt ansehen</span>
        </span>
      </span>
    </Link>
  ) : (
    <div className="tasks-toast__content">
      <Image src={infoIcon} alt="" width={24} height={24} />
      <div>
        <strong>{event.title}</strong>
        <div>{event.body}</div>
        <Link
          href={event.href}
          className="Notification-link"
          style={{ textDecoration: "underline" }}
          onClick={() => toast.dismiss(toastId)}
        >
          Jetzt ansehen.
        </Link>
      </div>
    </div>
  );

  toast.info(
    content,
    { toastId, containerId: "global", autoClose: 10000, closeOnClick: false, draggable: false,
      hideProgressBar: true, icon: false, closeButton: CloseButton, transition: Slide,
      className: isCounterOffer ? "tasks-toast tasks-toast--salesEvent" : "tasks-toast" },
  );
}

function AccountActivity({ accountKey, api, userData, children }) {
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const refresh = useRef(() => {});
  const ledgerRef = useRef(null);
  const {
    loading: overviewWonVehiclesLoading,
    vehicles: overviewWonVehicles,
  } = useFetchWonVehicles(userData);
  const invoiceContactId = getInvoiceContactIdForFrontend(userData);
  const [overdueState, setOverdueState] = useState({
    contactId: "",
    invoices: [],
    loading: true,
  });

  useEffect(() => {
    if (!invoiceContactId) {
      setOverdueState({ contactId: "", invoices: [], loading: false });
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setOverdueState({
      contactId: invoiceContactId,
      invoices: [],
      loading: true,
    });

    (async () => {
      try {
        const limit = 50;
        let offset = 0;
        let invoices = [];

        for (let pageIndex = 0; pageIndex < 5; pageIndex += 1) {
          const url =
            `${api}/sevdesk/invoices/by-contact` +
            `?contactId=${encodeURIComponent(invoiceContactId)}` +
            `&limit=${limit}` +
            `&offset=${offset}` +
            "&embed=contact" +
            "&include=positions,discount" +
            "&orderByInvoiceNumber=DESC" +
            "&status=offen";
          const authHeaders = await getDealerAuthHeaders();
          const response = await fetch(url, {
            credentials: "include",
            signal: controller.signal,
            headers: authHeaders,
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(
              data?.error || `Overview invoices failed (${response.status})`,
            );
          }
          const page = Array.isArray(data?.items) ? data.items : [];
          invoices = invoices.concat(page);
          if (!data?.page?.hasMore) break;
          offset += limit;
        }

        if (active) {
          setOverdueState({
            contactId: invoiceContactId,
            invoices: invoices.filter(isOverviewInvoiceOverdue),
            loading: false,
          });
        }
      } catch (fetchError) {
        if (fetchError?.name !== "AbortError") {
          console.error("[overview activity] fetch overdue invoices failed:", fetchError);
          if (active) {
            setOverdueState({
              contactId: invoiceContactId,
              invoices: [],
              loading: false,
            });
          }
        }
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [api, invoiceContactId]);

  useEffect(() => {
    let stopped = false;
    let inFlight = false;
    let failures = 0;
    let timer;
    let controller;
    const shownToasts = new Set();
    const storageKey = `ael:sales-activity:v1:${accountKey}`;
    const schedule = (delay) => { clearTimeout(timer); timer = setTimeout(poll, delay); };

    async function poll() {
      if (stopped || inFlight) return;
      if (document.visibilityState === "hidden") { schedule(10000); return; }
      inFlight = true;
      clearTimeout(timer);
      setBusy(true);
      setError("");
      controller = new AbortController();
      const requestController = controller;
      const timeout = setTimeout(() => requestController.abort(), 15000);
      try {
        const headers = await getDealerAuthHeaders();
        if (stopped) return;
        const response = await fetch(`${api}/dealer-web-activity`, {
          credentials: "include", headers, signal: requestController.signal, cache: "no-store",
        });
        if (!response.ok) throw new Error("Angebote konnten nicht geladen werden.");
        const data = await response.json();
        if (!isSalesActivityResponse(data)) throw new Error("Ungültige Angebotsdaten.");
        if (stopped) return;
        let previous = ledgerRef.current;
        try { previous = JSON.parse(localStorage.getItem(storageKey)) || previous; } catch { /* Storage may be disabled. */ }
        const { fresh, ledger } = consumeSalesEvents(data.events, previous, data.fetchedAt);
        ledgerRef.current = ledger;
        try { localStorage.setItem(storageKey, JSON.stringify(ledger)); } catch { /* In-memory deduplication remains active. */ }
        for (const event of fresh) {
          if (
            pathnameRef.current === "/marktplatz" &&
            event.type === "counter-offer"
          ) {
            continue;
          }
          const toastId = `sales:${accountKey}:${event.id}`;
          shownToasts.add(toastId);
          showEvent(event, toastId);
        }
        setSnapshot(data);
        setBusy(false);
        failures = 0;
        schedule(10000);
      } catch {
        if (stopped) return;
        failures += 1;
        if (failures < 5) {
          schedule(800 * 2 ** (failures - 1));
        } else {
          setBusy(false);
          setError("Angebote konnten nicht aktualisiert werden.");
          failures = 0;
          schedule(30000);
        }
      } finally {
        clearTimeout(timeout);
        inFlight = false;
      }
    }
    const retry = () => { failures = 0; poll(); };
    const resume = () => { if (document.visibilityState !== "hidden") poll(); };
    refresh.current = retry;
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    poll();
    return () => {
      stopped = true;
      refresh.current = () => {};
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      shownToasts.forEach((id) => toast.dismiss(id));
    };
  }, [accountKey, api]);

  useEffect(() => {
    pathnameRef.current = pathname;
    refresh.current();
  }, [pathname]);

  const tasks = (snapshot?.tasks || []).filter((task) => task.validUntil > Date.now());
  const overdueInvoices = overdueState.contactId === (invoiceContactId || "")
    ? overdueState.invoices
    : [];
  const overdueInvoicesLoading = Boolean(invoiceContactId)
    && (overdueState.contactId !== invoiceContactId || overdueState.loading);
  const overviewVehicleTaskCount = (overviewWonVehicles || []).filter((vehicle) =>
    ["price-confirmed", "pickup-confirmed"].includes(vehicle?.auction?.sellStatus),
  ).length;
  const overviewTaskCount =
    (userData?.companyInformation?.status === "registered" ? 1 : 0) +
    overviewVehicleTaskCount +
    overdueInvoices.length +
    tasks.length;

  return (
    <ActivityContext.Provider
      value={{
        tasks,
        loading: !snapshot && busy,
        refreshing: busy,
        error,
        retry: () => refresh.current(),
        overviewWonVehicles,
        overdueInvoices,
        overviewTaskCount,
        hasOverviewTask: overviewTaskCount > 0,
        overviewTasksLoading:
          overviewWonVehiclesLoading || overdueInvoicesLoading,
      }}
    >
      {children}
    </ActivityContext.Provider>
  );
}

export default function DealerWebActivityProvider({ children }) {
  const { user, userData, authInitialized } = useAuth();
  const api = useBackendUrl();
  const identity = user?.uid || userData?.uid || userData?.email;
  const enabled = authInitialized && identity && api && isDealerB2BEligible(userData);
  if (!enabled) return <ActivityContext.Provider value={EMPTY}>{children}</ActivityContext.Provider>;
  const accountKey = JSON.stringify([api, identity]);
  // Keying by identity also clears rendered data immediately during account switches.
  return (
    <AccountActivity
      key={accountKey}
      accountKey={accountKey}
      api={api}
      userData={userData}
    >
      {children}
    </AccountActivity>
  );
}

export const useDealerWebActivity = () => useContext(ActivityContext);
